import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, MessagePlugin, Popconfirm, Textarea } from 'tdesign-react';

import { ChangeMediaType, ContentType, EventToolBarType, ExclSaveType, SenderType } from '@/const/mission';
import { MediaConfigType } from '@/const/richMedia';
import { SEARCH_ID } from '@/const/search';
import { AppData } from '@/editor/flowEditor/control/types';
import { eventPool } from '@/event/eventPool';
import { useEventBus } from '@/hooks/useEventBus';
import { IEvents } from '@/interface';
import { ResourceTabType, UserResourceType } from '@/interface/library';
import { getRichLocalPath } from '@/material/form-upload/change-data';
import { customReport, CustomReportType } from '@/report/tam';
import { CgiErrorCode } from '@/services/cgi';
import { matchResourceList, UserResourceDetail } from '@/services/library';
import { generateMission } from '@/services/mission';

import { eventDefaultValue } from '../../toolbar/default';
import MyEditor from '../../toolbar/edit';
import { ToolbarItem } from '../../toolbar/toolbar';
import styles from '../index.module.less';
import { execData } from './util/execData';
import { getDefaultBotEvent } from './util/getDefaultBotEvent';
import { getParsedData, IResource } from './util/getParsedData';
import { getTips } from './util/getTips';

interface IProps {
  disabled: boolean;
  isMergeSceneMission: boolean | undefined;
  botList: { id: string; name: string }[];
  propsList: { id: string; name: string }[];
  shareInfo: AppData<Partial<unknown>>['data']['shareInfo']
  onChange: (value: any) => void;
  hasParse?: boolean;
  hasExcl?: boolean;
  isHorizontal: boolean;
  buttonDisabled?: boolean
  toolBar?: ToolbarItem[];
}

export enum IStatus {
  INIT = 'init',
  START = 'start',
  SUCCESS = 'success',
  FAIL = 'fail',
  END = 'end',
}

const ExclItem: React.FC<IProps> = (props) => {
  const {
    onChange,
    disabled,
    botList = [],
    shareInfo,
    isMergeSceneMission,
    hasExcl = false,
    hasParse = false,
    buttonDisabled = false,
    propsList = [],
    toolBar = [],
  } = props;
  const [status, setStatus] = useState(IStatus.INIT);
  const [list, setList] = useState<string[]>([]);
  const [sceneId, setSceneId] = useState<string>('');
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(false);

  const [batchMatch, setBatchMatch] = useState<any[]>([]);

  const [searchParams] = useSearchParams();
  const queryStoryId = searchParams.get(SEARCH_ID);
  const [exclLoading, setExclLoading] = useState(false);

  const [saveType, setSaveType] = useState<ExclSaveType>(ExclSaveType.EXCL_TYPE_DEFAULT);

  const { hasBot, sceneId: propsSceneId } = useMemo(() => {
    const hasBot = Boolean(shareInfo?.scene?.botPromptList?.map(e => e.id)?.length)
      || Boolean(shareInfo?.scene?.bots?.map(e => e.id)?.length);
    return {
      hasBot,
      sceneId: shareInfo?.scene?.id,
    };
  }, [
    shareInfo?.scene?.botPromptList,
    shareInfo?.scene?.bots,
  ]);

  // 消息通知立刻保存scene
  useEventBus(eventPool.eventsTypes.SAVE_SCENT_DETAIL, (payload: any) => {
    if (payload?.code === CgiErrorCode.NO_ERROR) {
      if (payload?.id) {
        setSceneId(payload?.id);
        setStatus(IStatus.SUCCESS);
      } else {
        setStatus(IStatus.FAIL);
      }
    } else {
      setStatus(IStatus.FAIL);
    }
  });

  // 消息通知立刻保存mission
  useEventBus(eventPool.eventsTypes.SAVE_MISSION_DETAIL, (payload: any) => {
    if (payload?.code === CgiErrorCode.NO_ERROR) {
      if (payload?.id) {
        setStatus(IStatus.SUCCESS);
      } else {
        setStatus(IStatus.FAIL);
      }
    } else {
      setStatus(IStatus.FAIL);
    }
  });

  // 获取编辑器内容
  const getList = (list: string[]) => {
    setList(list);
  };

  const matchPropList = (matchText: string) => {
    const prop = propsList?.find(e => matchText?.includes(e?.name));
    return isEmpty(prop) ? {} : prop;
  };

  // 开始解析
  const handleStart = async () => {
    if (loading) {
      MessagePlugin.error('智能创作中，请创作结束后使用剧本解析功能');
      return;
    }
    const empty = list.every(e => isEmpty(e?.trim()));
    if (empty) {
      MessagePlugin.error('请输入内容后点击剧本解析');
      return;
    }
    customReport(CustomReportType.MISSION_EXCL_INFO, {
      ['上报类型']: '开始解析',
      list,
    });
    setExclLoading(true);
    //  启动自动素材匹配能力
    const batchMatch: any[] = [];
    list?.forEach((e) => {
      const execValue = execData(e);
      const temp = getParsedData(execValue, matchBotList, matchPropList, undefined, toolBar);

      if (execValue?.text) {
        if (temp?.message?.type === ContentType.CONTENT_TYPE_TEXT && temp?.message?.text?.dubUrl?.name && execValue?.textDubUrlName) {
          batchMatch.push({
            type: UserResourceType.RESOURCE_TYPE_AUDIO,
            resourceName: execValue?.textDubUrlName,
          });
        }

        if (temp?.action?.changeMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_BACKGROUND_IMAGE
          || temp?.action?.changeMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_FOREGROUND_IMAGE
        ) {
          batchMatch.push({
            type: UserResourceType.RESOURCE_TYPE_IMAGE,
            resourceName: execValue?.text,
          });
        }

        if (temp?.action?.changeMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_BGM_AUDIO
        ) {
          batchMatch.push({
            type: UserResourceType.RESOURCE_TYPE_AUDIO,
            resourceName: execValue?.text,
          });
        }

        if (temp?.action?.changeMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_FULL_VIDEO
          || temp?.action?.changeMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_BACKGROUND_VIDEO
        ) {
          batchMatch.push({
            type: UserResourceType.RESOURCE_TYPE_VIDEO,
            resourceName: execValue?.text,
          });
        }

        if (temp?.action?.changeMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_SPINE_FORE_IMAGE
        ) {
          batchMatch.push({
            type: UserResourceType.RESOURCE_TYPE_SPINE,
            resourceName: execValue?.text,
          });
        }
      }
    });

    customReport(CustomReportType.MISSION_EXCL_INFO, {
      ['上报类型']: '开始解析-请求素材列表',
      batchMatch,
    });

    setBatchMatch(batchMatch);

    if (batchMatch?.length) {
      saveAndExcl();
    } else {
      endExclToEnd();
    }
  };

  const endExclToEnd = async (resource?: IResource) => {
    const data = list?.map((e) => {
      const execValue = execData(e);
      return getParsedData(execValue, matchBotList, matchPropList, resource, toolBar);
    });
    customReport(CustomReportType.MISSION_EXCL_INFO, {
      ['上报类型']: '匹配结果',
      data,
    });
    execList(data as IEvents[]);
  };

  const saveAndExcl = () => {
    if (isMergeSceneMission) {
      setSaveType(ExclSaveType.EXCL_TYPE_SCENE);
      setStatus(IStatus.START);
      eventPool.eventsEmit.saveScene(); // 先保存一下scene
    } else {
      setSaveType(ExclSaveType.EXCL_TYPE_MISSION);
      setStatus(IStatus.START);
      eventPool.eventsEmit.saveMission(); // 先保存一下mission
    }
  };

  const getResourceMatchToExcl = async () => {
    const resource = await getStoryResource(batchMatch);
    if (resource && resource?.code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error('素材数据拉取失败，请点击重新解析');
      setExclLoading(false);
      return;
    }
    endExclToEnd(resource);
  };

  useEffect(() => {
    if (saveType === ExclSaveType.EXCL_TYPE_SCENE) {
      if (status === IStatus.SUCCESS) {
        getResourceMatchToExcl();  // 开始匹配
        setSaveType(ExclSaveType.EXCL_TYPE_DEFAULT);
      } else if (status === IStatus.FAIL) {
        setExclLoading(false);
        setSaveType(ExclSaveType.EXCL_TYPE_DEFAULT);
        MessagePlugin.error('幕信息保存失败-解析失败，请点击重新解析');
      }
    }

    if (saveType === ExclSaveType.EXCL_TYPE_MISSION) {
      if (status === IStatus.SUCCESS) {
        getResourceMatchToExcl();// 开始匹配
        setSaveType(ExclSaveType.EXCL_TYPE_DEFAULT);
      } else if (status === IStatus.FAIL) {
        setExclLoading(false);
        setSaveType(ExclSaveType.EXCL_TYPE_DEFAULT);
        MessagePlugin.error('剧情信息保存失败-解析失败，请点击重新解析');
      }
    }
  }, [saveType, status, batchMatch]);

  // 获取故事纬度下的素材
  const getStoryResource = async (batchMatch: {
    type: UserResourceType,
    resourceName: string
  }[]) => {
    const { code, userResourceList = [] } = await matchResourceList({
      materialId: queryStoryId as unknown as string,
      tabType: ResourceTabType.TAB_TYPE_DEFAULT,
      batchMatch,
    });
    const imageList: UserResourceDetail & { exclName?: string }[] = [];
    const videoList: UserResourceDetail & { exclName?: string }[] = [];
    const audioList: UserResourceDetail & { exclName?: string }[] = [];
    const spineList: UserResourceDetail & { exclName?: string }[] = [];
    if (code === CgiErrorCode.NO_ERROR) {
      let resourceMaterialMaps: { [key: string]: string } = {};
      userResourceList?.forEach((e) => {
        if (e?.url) {
          const localPath = getRichLocalPath(e?.url || '') || '';
          resourceMaterialMaps = {
            ...resourceMaterialMaps,
            [localPath]: e?.url || '',
          };
        }
        return undefined;
      });

      eventPool.eventsEmit.materialChange({
        key: '',
        url: '',
        type: MediaConfigType.ARRAY, // 特殊的materiaMaps
        resourceMaterialMaps,
      });

      userResourceList?.map((e) => {
        if (e?.type === UserResourceType.RESOURCE_TYPE_IMAGE) {
          imageList.push({ ...e, exclName: e?.resourceName });
        }
        if (e?.type === UserResourceType.RESOURCE_TYPE_VIDEO) {
          videoList.push({ ...e, exclName: e?.resourceName });
        }
        if (e?.type === UserResourceType.RESOURCE_TYPE_AUDIO) {
          audioList.push({ ...e, exclName: e?.resourceName });
        }
        if (e?.type === UserResourceType.RESOURCE_TYPE_SPINE) {
          spineList.push({ ...e, exclName: e?.resourceName });
        }
      });
      const resourceObj = {
        imageList,
        videoList,
        audioList,
        spineList,
        code,
      };
      customReport(CustomReportType.MISSION_EXCL_INFO, {
        ['上报类型']: '开始解析-返回素材列表',
        resourceObj,
      });
      return resourceObj;
    }
    return {
      imageList,
      videoList,
      audioList,
      spineList,
      code,
    };
  };

  // 初始化scene信息，后端仅支持从ID获取信息，所以需要保证sceneId存在且是最新保存的数据
  const initScene = () => {
    setVisible(true);
    setSceneId('');
    setStatus(IStatus.START);
    eventPool.eventsEmit.saveScene();
  };

  // 开始智能创作
  const startGenerate = () => {
    handleClose();
    setLoading(true);
    const listValue = list.join('\n');
    let id = '';
    if (isMergeSceneMission) {
      id = sceneId;
    } else {
      id = propsSceneId || '';
    }
    generateMission(id, listValue).then((res) => {
      if (res === undefined) {
        return;
      }
      const data = res.split('\n').filter(item => !isEmpty(item))
        ?.map((e) => {
          const execValue = execData(e);
          return getParsedData(execValue, matchBotList, matchPropList, undefined, toolBar);
        });
      execList(data as IEvents[]);
    })
      .catch(() => { })
      .finally(() => {
        setLoading(false);
      });
  };

  // 智能创作
  const handleGenerate = () => {
    if (exclLoading) {
      MessagePlugin.error('剧本解析中，请解析结束后使用智能创作功能');
      return;
    }
    const listEmpty = list.every(e => isEmpty(e?.trim()));
    if (!hasBot) {
      MessagePlugin.error('请先关联“本幕角色”后，再点击智能创作');
      return;
    }
    if (listEmpty) {
      MessagePlugin.error('请输入有效剧情内容后点击智能创作');
      return;
    }
    if (isMergeSceneMission) {
      initScene();
    } else {
      startGenerate();
    }
  };

  // 解析剧本文字，进行数据转换
  const execList = (list: IEvents[]) => {
    const result = list?.map((data) => {
      if (!data || isEmpty(data)) {
        let defVal = eventDefaultValue?.[EventToolBarType.ADD_BOT];
        if (defVal?.message?.sender?.type === SenderType.BOT) {
          defVal = getDefaultBotEvent(defVal, botList);
        }
        return defVal;
      }

      if (data?.message?.sender?.type === SenderType.BOT) {
        return getDefaultBotEvent(data, botList);
      }

      return data;
    }).filter(e => e);
    setExclLoading(false);

    customReport(CustomReportType.MISSION_EXCL_INFO, {
      ['上报类型']: '最终解析结果',
      result,
    });

    onChange?.({
      type: EventToolBarType.ADD_DRAWER,
      value: result,
    });
  };

  // 匹配剧本文字中的角色
  const matchBotList = (matchText: string) => {
    const bot = botList?.find(e => matchText?.includes(e?.name));
    return bot?.id || botList?.[0]?.id || '';
  };

  // 关闭弹窗
  const handleClose = () => {
    setVisible(false);
    setSceneId('');
    setStatus(IStatus.INIT);
  };

  const { popDisabled, buttonText, content, confirm } = useMemo(() => ({
    content: () => {
      const tips = getTips(status, sceneId);
      return (
        <div className={styles.popTipContainer}>
          <div>{tips[0]}</div>
          <p>{tips[1]}</p>
        </div>
      );
    },
    popDisabled: status === IStatus.START,
    buttonText: status === IStatus.FAIL ? '重试' : '确定',
    confirm: status === IStatus.FAIL ? handleGenerate : startGenerate,
  }), [status, sceneId]);

  return (
    <div className={styles.itemBox}>
      <div className={classNames(
        styles.contentBox,
        styles.exclBox,
        { ['dream-focus']: focus },
      )}>
        {disabled && !buttonDisabled ? <Textarea disabled={disabled} autosize={{ minRows: 4 }} />
          : <MyEditor setFocus={setFocus} getList={getList} disabled={disabled} />
        }
        <div className={styles.execBtn}>
          {
            hasExcl && (isMergeSceneMission
              ? <Popconfirm
                icon={null}
                content={content()}
                destroyOnClose
                className={styles.popConfirm}
                placement="top"
                theme="default"
                showArrow
                onCancel={handleClose}
                confirmBtn={
                  <Button
                    size="small"
                    variant="base"
                    disabled={popDisabled || buttonDisabled}
                    onClick={confirm}
                  >
                    {buttonText}
                  </Button>
                }
                visible={visible}
              >
                <Button
                  theme="primary"
                  onClick={handleGenerate}
                  disabled={disabled || buttonDisabled}
                  className={styles.btn}
                  loading={loading}
                >
                  智能创作
                </Button>
              </Popconfirm>
              : <Button
                theme="primary"
                onClick={handleGenerate}
                disabled={disabled || buttonDisabled}
                className={styles.btn}
                loading={loading}
              >
                智能创作
              </Button>)
          }
          {hasParse && <Button
            theme="primary"
            onClick={handleStart}
            disabled={disabled || buttonDisabled}
            className={styles.btn}
            loading={exclLoading}
          >
            剧本解析
          </Button>}
        </div>
      </div>
    </div>
  );
};

export default ExclItem;
